import {
  PlanTier,
  UserDto,
  UserOrganizationDto,
  WorkspaceDto,
  WorkspaceEnvironmentDto,
} from '@flagsync/skyline';

import { ServiceErrorCode } from '@/services/utils/service-error';

export interface SdkResponseTime {
  server: number;
  client: number;
}

export enum SuccessCriteria {
  Increase = 'inc',
  Decrease = 'dec',
}

export enum UsageMetricTypeCode {
  MAU = 'MAU',
  MRC = 'MRC',
}

export enum TrackDataItemType {
  Event = 'event',
  Impression = 'impression',
}

export enum MetricMeasurement {
  Count = 'cnt',
  Sum = 'sum',
  Average = 'avg',
  Percent = 'pct',
}

export type MetricMeasurementUnion =
  (typeof MetricMeasurement)[keyof typeof MetricMeasurement];

export enum AuditEntityType {
  FeatureFlag = 'feature_flag',
}

export enum AuditEventType {
  Flag_Create = 'flag_create',
  Flag_Archive = 'flag_archive',
  Flag_UpdateState = 'flag_update_state',
  Flag_AddVariantTargetingRule = 'flag_add_variant_targeting_rule',
  Flag_UpdateVariantTargetingRule = 'flag_update_variant_targeting_rule',
  Flag_RemoveVariantTargetingRule = 'flag_remove_variant_targeting_rule',
  Flag_UpdateVariants = 'flag_update_variants',
  Flag_AddClausesToTargetingRule = 'flag_add_clauses_to_targeting_rule',
  Flag_RemoveClausesFromTargetingRule = 'flag_remove_clauses_from_targeting_rule',
  Flag_UpdateVariantRollout = 'flag_update_variant_rollout',
  Flag_UpdatePercentageRollout = 'flag_update_percentage_rollout',
}

export enum TargetingRuleClauseOperatorCode {
  IS_ONE_OF = 'IOO',
  IS_NOT_ONE_OF = 'INOO',
  ENDS_WITH = 'EW',
  DOES_NOT_END_WITH = 'DNEW',
  STARTS_WITH = 'SW',
  DOES_NOT_START_WITH = 'DNSW',
  MATCHES_REGEX = 'MR',
  DOES_NOT_MATCH_REGEX = 'DNMR',
  LESS_THAN = 'LT',
  LESS_THAN_OR_EQUAL = 'LTE',
  GREATER_THAN = 'GT',
  GREATER_THAN_OR_EQUAL = 'GTE',
  SEMVER_LESS_THAN = 'SVLT',
  SEMVER_LESS_THAN_OR_EQUAL = 'SVLTE',
  SEMVER_GREATER_THAN = 'SVGT',
  SEMVER_GREATER_THAN_OR_EQUAL = 'SVGTE',
}

export type TargetingRuleClauseOperatorCodeUnion =
  (typeof TargetingRuleClauseOperatorCode)[keyof typeof TargetingRuleClauseOperatorCode];

export enum TargetingRuleClauseAttributeCode {
  KEY = 'KEY',
  CUSTOM = 'CUSTOM',
}

export type TargetingRuleClauseAttributeCodeUnion =
  (typeof TargetingRuleClauseAttributeCode)[keyof typeof TargetingRuleClauseAttributeCode];

export enum ExperimentState {
  Active = 'Active',
  Inactive = 'Inactive',
}

export enum DataTypeCode {
  BOOL = 'BOOL',
  STR = 'STR',
  NUM = 'NUM',
  JSON = 'JSON',
}

export const DataTypeCodes = [
  DataTypeCode.BOOL,
  DataTypeCode.STR,
  DataTypeCode.NUM,
  DataTypeCode.JSON,
] as const;

export type DataTypeCodeUnion = keyof typeof DataTypeCode;

export enum ImpressionTimeframe {
  lastHour = 'lastHour',
  lastDay = 'lastDay',
  last7days = 'last7days',
  last14days = 'last14days',
  last30days = 'last30days',
  last60days = 'last60days',
}

export type ImpressionTimeframeUnion = keyof typeof ImpressionTimeframe;

export enum UserOnboardingStatusCode {
  PENDING = 'PENDING',
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETE = 'COMPLETE',
}

export const DataTypeMap = {
  [DataTypeCode.BOOL]: 'Boolean',
  [DataTypeCode.STR]: 'String',
  [DataTypeCode.NUM]: 'Number',
  [DataTypeCode.JSON]: 'JSON',
} as const;

export const KeyTypeCode = {
  SERVER: 'SERVER',
  CLIENT: 'CLIENT',
} as const;

export type KeyTypeCodeUnion = keyof typeof KeyTypeCode;

export const TokenTypeCode = {
  VER_ACC: 'VER_ACC',
  RES_PWD: 'RES_PWD',
  ORG_INV: 'ORG_INV',
} as const;

export type TokenTypeCodeUnion = keyof typeof TokenTypeCode;

export enum JobTitleCode {
  PRODUCT_MANAGER = 'PM',
  ENGINEERING_MANAGER = 'EM',
  SOFTWARE_ENGINEER = 'SE',
  DATA_SCIENTIST = 'DS',
  QA = 'QA',
  EXECUTIVE = 'EX',
  OTHER = 'OT',
}

export enum UserRoleCode {
  VIEWER = 'VWR',
  EDITOR = 'EDT',
  OWNER = 'OWN',
  GOD = 'GOD',
}

/** OWN and GOD are owner-equivalent: full access everywhere. */
export const isOwnerRole = (code: UserRoleCode | `${UserRoleCode}`): boolean =>
  code === UserRoleCode.OWNER || code === UserRoleCode.GOD;

export interface AppContextIdMap {
  organizationId: number;
  workspaceId: number;
  environmentId: number;
}

export interface AppContext {
  user: UserDto;
  organization: UserOrganizationDto;
  workspace: WorkspaceDto;
  environment: WorkspaceEnvironmentDto;
  planTier: PlanTier | undefined;
  /**
   * True when this session is a read-only God "View-as" session. `user` is then
   * the impersonated target; the UI shows a banner and treats itself as
   * read-only (the API enforces it regardless).
   */
  viewAs: boolean;
}

export interface ValidationError {
  /**
   * Object that was validated.
   *
   * OPTIONAL - configurable via the ValidatorOptions.validationError.target option
   */
  target?: Record<string, any>;
  /**
   * Object's property that hasn't passed validation.
   */
  property: string;
  /**
   * Value that haven't pass a validation.
   *
   * OPTIONAL - configurable via the ValidatorOptions.validationError.value option
   */
  value?: any;
  /**
   * Constraints that failed validation with error messages.
   */
  constraints?: {
    [type: string]: string;
  };
  /**
   * Contains all nested validation errors of the property.
   */
  children?: ValidationError[];
  /**
   * A transient set of data passed through to the validation result for response mapping
   */
  contexts?: {
    [type: string]: any;
  };
}

export interface SkylineErrorResponse {
  statusCode: number;
  message: ServiceErrorCode;
  path: string;
  timestamp: string;
  validationErrors: ValidationError[];
  metadata: Record<string, any>;
}
